import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { FiRefreshCw, FiFilter } from 'react-icons/fi';
import { HiDocumentReport } from 'react-icons/hi';
import { FaRegFilePdf, FaRegFileExcel } from 'react-icons/fa';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import instanciaAxios, { esErrorPermiso } from '../../miscelaneo/instanciaAxios';
import SpinnerCargando from '../util/SpinnerCargando';
import BotonExportar from '../util/BotonExportar';
import GraficoVentasMes from '../dashboard/GraficoVentasMes';
import generarPDFReporteVentas, { generarPDFReporteVentasDetallado } from './generarPDFReporteVentas';
import generarExcelReporteVentas, { generarExcelReporteVentasDetallado } from './generarExcelReporteVentas';

const ESTADOS_FACTURA = [
  { value: '', label: 'Todos los estados' },
  { value: '1', label: 'Activa' },
  { value: '4', label: 'Cerrada' },
  { value: '2', label: 'Inactiva' }
];

function primerDiaMesActual() {
  const hoy = new Date();
  return `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-01`;
}

function hoyLocal() {
  const hoy = new Date();
  const yyyy = hoy.getFullYear();
  const mm = String(hoy.getMonth() + 1).padStart(2, '0');
  const dd = String(hoy.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function formatMonto(monto) {
  return parseFloat(monto || 0).toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatFecha(fecha) {
  if (!fecha) return '-';
  const [y, m, d] = fecha.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('es-DO', { day: '2-digit', month: 'short', year: 'numeric' });
}

function TarjetaMetrica({ titulo, valor, colorBorde, colorTexto }) {
  return (
    <div className={`bg-white rounded-xl border shadow-md p-4 border-l-4 ${colorBorde}`}>
      <p className="text-sm text-gray-600 font-medium mb-1">{titulo}</p>
      <p className={`text-xl font-bold ${colorTexto}`}>{valor}</p>
    </div>
  );
}

function TooltipBarra({ active, payload }) {
  if (active && payload && payload.length) {
    const item = payload[0].payload;
    return (
      <div className="bg-color-primario text-white px-4 py-2 rounded-lg shadow-lg text-sm">
        <p className="font-medium">{item.descripcion_completa}</p>
        <p className="text-green-200">RD$ {formatMonto(item.monto_total)}</p>
        <p className="text-green-200 text-xs">{item.cantidad_facturas} factura(s)</p>
      </div>
    );
  }
  return null;
}

export default function ReporteVentas() {
  const [datos, setDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [exportandoResumen, setExportandoResumen] = useState(false);
  const [exportandoDetalle, setExportandoDetalle] = useState(false);
  const [catalogos, setCatalogos] = useState({ sucursales: [], monedas: [], tiposComprobante: [] });
  const [filtros, setFiltros] = useState({
    fecha_desde: primerDiaMesActual(),
    fecha_hasta: hoyLocal(),
    id_sucursal: '',
    id_tipo_comprobante_fiscal: '',
    id_estado: '',
    id_moneda: '',
    solo_con_comprobante: false
  });

  useEffect(() => {
    const cargarCatalogos = async () => {
      try {
        const [sucursalesRes, monedasRes, tiposRes] = await Promise.all([
          instanciaAxios.get('/miscelaneo/sucursal'),
          instanciaAxios.get('/miscelaneo/moneda'),
          instanciaAxios.get('/comprobantefiscal/tipo')
        ]);
        setCatalogos({
          sucursales: sucursalesRes.data || [],
          monedas: monedasRes.data || [],
          tiposComprobante: tiposRes.data || []
        });
      } catch (error) {
        console.error('Error al cargar catálogos del reporte de ventas:', error);
      }
    };
    cargarCatalogos();
  }, []);

  const cargarDatos = useCallback(async () => {
    try {
      setCargando(true);
      const params = Object.fromEntries(Object.entries(filtros).filter(([, v]) => v !== '' && v !== false));
      const response = await instanciaAxios.get('/reporte/ventas-resumen', { params });
      setDatos(response.data);
    } catch (error) {
      if (esErrorPermiso(error)) return;
      console.error('Error al cargar el reporte de ventas:', error);
      toast.error('Error al cargar el reporte de ventas');
    } finally {
      setCargando(false);
    }
  }, [filtros]);

  useEffect(() => {
    cargarDatos();
  }, [cargarDatos]);

  const handleFiltroChange = (campo, valor) => {
    setFiltros(prev => ({ ...prev, [campo]: valor }));
  };

  const handleExportarResumenPDF = () => {
    if (!datos) return;
    try {
      generarPDFReporteVentas(datos, filtros);
    } catch (error) {
      console.error('Error al generar el PDF del reporte de ventas:', error);
      toast.error('Error al generar el PDF');
    }
  };

  const handleExportarResumenExcel = async () => {
    if (!datos) return;
    try {
      setExportandoResumen(true);
      await generarExcelReporteVentas(datos, filtros);
    } catch (error) {
      console.error('Error al generar el Excel del reporte de ventas:', error);
      toast.error('Error al generar el Excel');
    } finally {
      setExportandoResumen(false);
    }
  };

  const obtenerDetalleFacturas = async () => {
    const params = Object.fromEntries(
      Object.entries(filtros).filter(([campo, v]) => v !== '' && v !== false && campo !== 'solo_con_comprobante')
    );
    const response = await instanciaAxios.get('/reporte/ventas-detallado', { params });
    if (response.data.truncado) {
      toast.warning(`Se exportaron solo las primeras ${response.data.facturas.length} facturas de un total de ${response.data.total_facturas}.`);
    }
    return response.data;
  };

  const handleExportarDetallePDF = async () => {
    try {
      setExportandoDetalle(true);
      const datosDetalle = await obtenerDetalleFacturas();
      generarPDFReporteVentasDetallado(datosDetalle, filtros);
    } catch (error) {
      if (esErrorPermiso(error)) return;
      console.error('Error al exportar el detalle de facturas:', error);
      toast.error('Error al exportar el detalle de facturas');
    } finally {
      setExportandoDetalle(false);
    }
  };

  const handleExportarDetalleExcel = async () => {
    try {
      setExportandoDetalle(true);
      const datosDetalle = await obtenerDetalleFacturas();
      await generarExcelReporteVentasDetallado(datosDetalle, filtros);
    } catch (error) {
      if (esErrorPermiso(error)) return;
      console.error('Error al exportar el detalle de facturas:', error);
      toast.error('Error al exportar el detalle de facturas');
    } finally {
      setExportandoDetalle(false);
    }
  };

  if (cargando && !datos) {
    return <SpinnerCargando />;
  }

  const tendenciaGrafico = (datos?.tendencia || []).map(t => ({
    dia: formatFecha(t.periodo),
    ventas: t.monto_total
  }));

  return (
    <div className="bg-gray-200 min-h-screen">
      <div className="flex justify-center py-8">
        <div className="bg-gray-100 bg-opacity-90 rounded-xl border border-gray-100 p-8 w-full max-w-7xl mx-6">
          <div className="flex flex-col gap-6">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-3">
                  <HiDocumentReport className="text-color-primario text-3xl" />
                  <h1 className="font-cascadia font-bold text-2xl">Reporte de Ventas</h1>
                </div>
                <p className="text-gray-600 text-base">
                  {formatFecha(filtros.fecha_desde)} - {formatFecha(filtros.fecha_hasta)}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <BotonExportar
                  texto="Exportar Resumen"
                  disabled={!datos}
                  cargando={exportandoResumen}
                  variante="solido"
                  opciones={[
                    { label: 'Formato PDF', icono: <FaRegFilePdf className="text-red-500" size={16} />, onClick: handleExportarResumenPDF },
                    { label: 'Formato Excel', icono: <FaRegFileExcel className="text-green-600" size={16} />, onClick: handleExportarResumenExcel }
                  ]}
                />
                <BotonExportar
                  texto="Exportar Detalle (NCF)"
                  disabled={!datos}
                  cargando={exportandoDetalle}
                  variante="outline"
                  opciones={[
                    { label: 'Formato PDF', icono: <FaRegFilePdf className="text-red-500" size={16} />, onClick: handleExportarDetallePDF },
                    { label: 'Formato Excel', icono: <FaRegFileExcel className="text-green-600" size={16} />, onClick: handleExportarDetalleExcel }
                  ]}
                />
                <button
                  onClick={cargarDatos}
                  disabled={cargando}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-200 rounded-lg transition-colors print:hidden"
                  title="Actualizar"
                >
                  <FiRefreshCw size={20} className={cargando ? 'animate-spin' : ''} />
                </button>
              </div>
            </div>

            {/* Filtros */}
            <div className="bg-white rounded-xl border border-gray-300 shadow-md p-4 print:hidden">
              <div className="flex items-center gap-2 text-gray-700 mb-3">
                <FiFilter className="text-lg" />
                <label className="font-medium text-base">Filtros</label>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Desde</label>
                  <input
                    type="date"
                    value={filtros.fecha_desde}
                    onChange={(e) => handleFiltroChange('fecha_desde', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-400 rounded-lg focus:ring-2 focus:ring-color-primario focus:border-color-primario transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Hasta</label>
                  <input
                    type="date"
                    value={filtros.fecha_hasta}
                    onChange={(e) => handleFiltroChange('fecha_hasta', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-400 rounded-lg focus:ring-2 focus:ring-color-primario focus:border-color-primario transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Sucursal</label>
                  <select
                    value={filtros.id_sucursal}
                    onChange={(e) => handleFiltroChange('id_sucursal', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-400 rounded-lg focus:ring-2 focus:ring-color-primario focus:border-color-primario transition-colors"
                  >
                    <option value="">Todas las sucursales</option>
                    {catalogos.sucursales.map(s => (
                      <option key={s.id_sucursal} value={s.id_sucursal}>{s.descripcion}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Tipo de Comprobante</label>
                  <select
                    value={filtros.id_tipo_comprobante_fiscal}
                    onChange={(e) => handleFiltroChange('id_tipo_comprobante_fiscal', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-400 rounded-lg focus:ring-2 focus:ring-color-primario focus:border-color-primario transition-colors"
                  >
                    <option value="">Todos los tipos</option>
                    {catalogos.tiposComprobante.map(t => (
                      <option key={t.id_tipo_comprobante_fiscal} value={t.id_tipo_comprobante_fiscal}>{t.descripcion}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Estado</label>
                  <select
                    value={filtros.id_estado}
                    onChange={(e) => handleFiltroChange('id_estado', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-400 rounded-lg focus:ring-2 focus:ring-color-primario focus:border-color-primario transition-colors"
                  >
                    {ESTADOS_FACTURA.map(e => (
                      <option key={e.value} value={e.value}>{e.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Moneda</label>
                  <select
                    value={filtros.id_moneda}
                    onChange={(e) => handleFiltroChange('id_moneda', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-400 rounded-lg focus:ring-2 focus:ring-color-primario focus:border-color-primario transition-colors"
                  >
                    <option value="">Todas las monedas</option>
                    {catalogos.monedas.map(m => (
                      <option key={m.id_moneda} value={m.id_moneda}>{m.descripcion}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3">
                <input
                  type="checkbox"
                  id="solo_con_comprobante"
                  checked={filtros.solo_con_comprobante}
                  onChange={(e) => handleFiltroChange('solo_con_comprobante', e.target.checked)}
                  className="h-4 w-4 text-color-primario border-gray-400 rounded focus:ring-color-primario"
                />
                <label htmlFor="solo_con_comprobante" className="text-sm text-gray-700 cursor-pointer">
                  Solo facturas con comprobante fiscal (NCF)
                </label>
              </div>
            </div>

            {datos && (
              <>
                {/* RESUMEN EJECUTIVO */}
                <div className="space-y-4">
                  <h2 className="font-cascadia text-xl font-bold text-gray-800">Resumen Ejecutivo</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <TarjetaMetrica
                      titulo="Total de Ventas (Neto)"
                      valor={`RD$ ${formatMonto(datos.resumen_general.total_ventas_neto)}`}
                      colorBorde="border-green-500"
                      colorTexto="text-green-700"
                    />
                    <TarjetaMetrica
                      titulo="Cantidad de Facturas"
                      valor={datos.resumen_general.cantidad_facturas.toLocaleString('es-DO')}
                      colorBorde="border-blue-500"
                      colorTexto="text-blue-700"
                    />
                    <TarjetaMetrica
                      titulo="Ticket Promedio"
                      valor={`RD$ ${formatMonto(datos.resumen_general.ticket_promedio)}`}
                      colorBorde="border-purple-500"
                      colorTexto="text-purple-700"
                    />
                    <TarjetaMetrica
                      titulo="Dinero Recibido"
                      valor={`RD$ ${formatMonto(datos.dinero_recibido.total_cobrado)}`}
                      colorBorde="border-teal-500"
                      colorTexto="text-teal-700"
                    />
                    <TarjetaMetrica
                      titulo="Dinero por Cobrar"
                      valor={`RD$ ${formatMonto(datos.dinero_por_cobrar.total_pendiente)}`}
                      colorBorde="border-orange-500"
                      colorTexto="text-orange-700"
                    />
                    <TarjetaMetrica
                      titulo={`Ventas al Contado (${datos.ventas_credito_contado.contado.porcentaje}%)`}
                      valor={`RD$ ${formatMonto(datos.ventas_credito_contado.contado.monto_total)}`}
                      colorBorde="border-emerald-500"
                      colorTexto="text-emerald-700"
                    />
                    <TarjetaMetrica
                      titulo={`Ventas a Crédito (${datos.ventas_credito_contado.credito.porcentaje}%)`}
                      valor={`RD$ ${formatMonto(datos.ventas_credito_contado.credito.monto_total)}`}
                      colorBorde="border-amber-500"
                      colorTexto="text-amber-700"
                    />
                    <TarjetaMetrica
                      titulo={`Ventas Gratuitas (${datos.ventas_credito_contado.gratuito.porcentaje}%)`}
                      valor={`RD$ ${formatMonto(datos.ventas_credito_contado.gratuito.monto_total)}`}
                      colorBorde="border-gray-400"
                      colorTexto="text-gray-700"
                    />
                  </div>
                </div>

                {/* TENDENCIA */}
                <GraficoVentasMes
                  datos={tendenciaGrafico}
                  dataKey="ventas"
                  etiquetaValor="Ventas"
                  titulo="Tendencia de Ventas"
                  badgeTexto={`${formatFecha(filtros.fecha_desde)} - ${formatFecha(filtros.fecha_hasta)}`}
                  emptyText="Sin datos de ventas en el período seleccionado"
                />

                {/* POR TIPO DE COMPROBANTE */}
                <div className="space-y-4">
                  <h2 className="font-cascadia text-xl font-bold text-gray-800">Ventas por Tipo de Comprobante</h2>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="bg-white rounded-xl border border-gray-300 shadow-md p-6">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Tipo</th>
                              <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider">Facturas</th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">Subtotal</th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">ITBIS</th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">Monto Total</th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">%</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {datos.por_tipo_comprobante.map((tipo, idx) => (
                              <tr key={idx} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm font-medium text-gray-900">{tipo.descripcion_completa}</td>
                                <td className="px-4 py-3 text-sm text-gray-600 text-center">{tipo.cantidad_facturas}</td>
                                <td className="px-4 py-3 text-sm text-gray-900 text-right">RD$ {formatMonto(tipo.monto_bruto)}</td>
                                <td className="px-4 py-3 text-sm text-gray-900 text-right">RD$ {formatMonto(tipo.monto_impuesto)}</td>
                                <td className="px-4 py-3 text-sm text-gray-900 text-right font-semibold">RD$ {formatMonto(tipo.monto_total)}</td>
                                <td className="px-4 py-3 text-sm text-gray-600 text-right">{tipo.porcentaje_del_total}%</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                      <h3 className="text-base font-semibold text-gray-800 mb-4">Monto por Tipo</h3>
                      <div className="w-full h-72">
                        {datos.por_tipo_comprobante.length === 0 ? (
                          <div className="flex items-center justify-center h-full text-gray-400 text-sm">
                            Sin datos para mostrar
                          </div>
                        ) : (
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              data={datos.por_tipo_comprobante}
                              layout="vertical"
                              margin={{ top: 5, right: 20, left: 10, bottom: 0 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                              <XAxis
                                type="number"
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 12, fill: '#9ca3af' }}
                                tickFormatter={(value) => value >= 1000 ? `${(value / 1000).toFixed(0)}k` : value}
                              />
                              <YAxis
                                type="category"
                                dataKey="descripcion_completa"
                                axisLine={false}
                                tickLine={false}
                                width={110}
                                tick={{ fontSize: 11, fill: '#374151' }}
                              />
                              <Tooltip content={<TooltipBarra />} cursor={{ fill: '#f3f4f6' }} />
                              <Bar dataKey="monto_total" fill="#39806A" radius={[0, 4, 4, 0]} maxBarSize={22} />
                            </BarChart>
                          </ResponsiveContainer>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* DINERO RECIBIDO */}
                {datos.dinero_recibido.por_metodo_pago.length > 0 && (
                  <div className="space-y-4">
                    <h2 className="font-cascadia text-xl font-bold text-gray-800">Dinero Recibido por Método de Pago</h2>
                    <div className="bg-white rounded-xl border border-gray-300 shadow-md p-6">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider">Método</th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">Monto Total</th>
                              <th className="px-4 py-3 text-center text-xs font-medium text-gray-600 uppercase tracking-wider">Transacciones</th>
                              <th className="px-4 py-3 text-right text-xs font-medium text-gray-600 uppercase tracking-wider">%</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {datos.dinero_recibido.por_metodo_pago.map((metodo, idx) => (
                              <tr key={idx} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm font-medium text-gray-900">{metodo.metodo_pago}</td>
                                <td className="px-4 py-3 text-sm text-gray-900 text-right font-semibold">RD$ {formatMonto(metodo.monto_total)}</td>
                                <td className="px-4 py-3 text-sm text-gray-600 text-center">{metodo.cantidad_transacciones}</td>
                                <td className="px-4 py-3 text-sm text-gray-600 text-right">{metodo.porcentaje}%</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}

                {/* DINERO POR COBRAR */}
                <div className="space-y-4">
                  <h2 className="font-cascadia text-xl font-bold text-gray-800">Cuentas por Cobrar</h2>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <TarjetaMetrica
                      titulo="Vencidas"
                      valor={`RD$ ${formatMonto(datos.dinero_por_cobrar.vencidas.total)} (${datos.dinero_por_cobrar.vencidas.cantidad})`}
                      colorBorde="border-red-500"
                      colorTexto="text-red-700"
                    />
                    <TarjetaMetrica
                      titulo="Por Vencer (7 días)"
                      valor={`RD$ ${formatMonto(datos.dinero_por_cobrar.por_vencer_7_dias.total)} (${datos.dinero_por_cobrar.por_vencer_7_dias.cantidad})`}
                      colorBorde="border-yellow-500"
                      colorTexto="text-yellow-700"
                    />
                    <TarjetaMetrica
                      titulo="Vigentes"
                      valor={`RD$ ${formatMonto(datos.dinero_por_cobrar.vigentes.total)} (${datos.dinero_por_cobrar.vigentes.cantidad})`}
                      colorBorde="border-green-500"
                      colorTexto="text-green-700"
                    />
                  </div>
                </div>

              </>
            )}

            {/* Sin datos */}
            {datos && datos.resumen_general.cantidad_facturas === 0 && (
              <div className="bg-white rounded-xl border border-gray-300 shadow-md p-12 text-center">
                <p className="text-gray-500 text-lg">No hay ventas registradas para el período seleccionado</p>
                <p className="text-gray-400 text-base mt-2">Ajusta los filtros para ver otro rango o criterio</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
